import React, { useState, useEffect, useMemo, useRef, useContext, useCallback } from "react";
import { createPortal } from "react-dom";
import cn from "classnames";
import { Message_data } from "../context/context.js";
import { useTracks } from "../context/TracksContext";
import { db, db2, auth, storage, provider } from "../firebase-config.js";
import { awardRequestPoint, subscribePoints } from "../services/pointsService.js";
import { cleanYoutubeMeta } from "../services/metadataClean.js";
import { backfillGenre } from "../services/genreService.js";
import { searchDjLibrary } from "../services/djLibraryService.js";
import { linkWithPopup, GoogleAuthProvider, signInWithCredential } from "firebase/auth";
import {
  doc,
  getDoc,
  getDocs,
  addDoc,
  setDoc,
  updateDoc,
  deleteField,
  collection,
  onSnapshot,
  query,
  serverTimestamp,
  where,
  orderBy,
  limit,
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { QRCodeSVG } from "qrcode.react";
import { groupTracksByGenre } from "../lib/grouptracksByGenre.js";
import { getCurrentPrize, findWinningPrize, claimPrize } from "../services/prizeDrawService.js";
import { recordPrizeWin, prizeQrPayload, prizeCode } from "../services/prizeArchiveService.js";
import { clearAnonCredentials } from "../services/anonCredentialBackup.js";
import { recordRequestAndMaybePrompt } from "../services/anonLinkPrompt";
import { useLiveGuestDj } from "../hooks/useLiveGuestDj";
import GenreList2 from "./GenreList2.js";
import EditUser from "./EditUser4.js";
import MoodSelector from "./MoodSelector.js";
import TipBurst from "./TipBurst.js";
import AccordionList from "./AccordionListManuf";
import { JukeboxList4 } from "./JukeboxList4.js";
import { NextTrackVote } from "./NextTrackVote3.js";
import { CurrentTrackView } from "./CurrentTrackView.js";
import { ClientInfo } from "./ClientInfo.js";
import { NowPlaying } from "./NowPlaying.js";
import ChatV2 from "./ChatV2";
import useVideos from "../hooks/useVideos";
import { FALLBACK_COVERART } from "../lib/fallbackCoverart";
import "../styles/Chat.scss";

export const Jukebox4 = ({
  userObj,
  clientObj,
  openJukeboxPlaylist,
  showChatlist,
  chatScreen,
  finishChat,
  isInUsers,
  closeChat,
  setChatScreenHeader,
  navigateToPlaying,
  setNavigateToPlaying,
}) => {
  const chatbase = useRef(null);
  const { message, setMessage } = useContext(Message_data);
  const { setTracks } = useTracks();
  const [jukebox, setJukebox] = useState([]);
  const [tab, setTab] = useState(() => {
    // Check if an initial tab was requested via URL param (from iframe embed)
    const initialTab = sessionStorage.getItem('muuzbox_initial_tab');
    if (initialTab) {
      sessionStorage.removeItem('muuzbox_initial_tab');
      return initialTab;
    }
    return "requests"; // Default: Start on Request page
  });
  const jukeboxRef = collection(db2, "jukebox");
  const userJukeboxRef = collection(
    db,
    "users",
    clientObj.clientId, //'FsWFsMVsahQA0Q3JOYD0vnpRUeh1',  //default DJ User, currently conwayalphonso7 // thisisobah: rjrmFwJO0ccM0p338sJG4WaDPpS2
    "playlists"
  );
  const [inputFocus, setInputFocus] = useState(false);
  const [originalJukebox, setOriginalJukebox] = useState([]);
  const [count, setCount] = useState(originalJukebox.length);
  const [isOpen, setIsOpen] = useState(false);
  // Loyalty points shown in the request-confirmation modal + the delayed
  // progress-bar animation trigger.
  const [points, setPoints] = useState(0);
  const [animateProgress, setAnimateProgress] = useState(false);
  // iOS has no Vibration API, so the modal also "shivers" for ~0.5s in time
  // with the haptic tap so those users get an equivalent visual cue.
  const [shiver, setShiver] = useState(false);
  const [showPrizeWinner, setShowPrizeWinner] = useState(false);
  const [prizeWinDetails, setPrizeWinDetails] = useState(null);
  // Just-requested track (identity + one-shot nonce) → NowPlaying flashes it in
  // the queue and floats a vote reminder when the guest lands there.
  const [pendingHighlight, setPendingHighlight] = useState(null);
  const clearPendingHighlight = useCallback(() => setPendingHighlight(null), []);
  // Prize-claim flow: { djchatId, prizeId, isAnonymous } + a status machine
  // ('prompt' | 'claiming' | 'claimed' | 'already_won' | 'forfeited' | 'error').
  const [prizeClaim, setPrizeClaim] = useState(null);
  const [prizeClaimStatus, setPrizeClaimStatus] = useState("prompt");
  const [claimError, setClaimError] = useState("");
  // Bumped when the prize-win modal appears → fires the money/stars celebration.
  const [prizeCelebrate, setPrizeCelebrate] = useState(0);

  // Prize win → celebrate with the same haptic buzz as the request modal plus
  // the "Tip the DJ" money/stars rain.
  useEffect(() => {
    if (!showPrizeWinner) return;
    try { if (navigator.vibrate) navigator.vibrate([400, 100, 400, 100, 400]); } catch (_) {}
    setPrizeCelebrate((n) => n + 1);
  }, [showPrizeWinner]);

  // Claim a won prize. For unlinked guests this first runs the Google
  // account-link popup (that IS the "attach your account" requirement),
  // then atomically claims the prize (first come, first served).
  const doClaimPrize = async (claimArg) => {
    const c = claimArg || prizeClaim;
    if (!c || !auth.currentUser) return;
    setClaimError("");
    setPrizeClaimStatus("claiming");
    try {
      if (c.isAnonymous) {
        let linkedUid;
        try {
          // Preferred path: attach Google to the existing anonymous uid so all
          // their history stays put.
          const result = await linkWithPopup(auth.currentUser, provider);
          linkedUid = result.user?.uid || auth.currentUser?.uid;
        } catch (linkErr) {
          const code = linkErr?.code || "";
          // The chosen Google account is already a Muuzbox user — we can't
          // link it onto the guest, but we CAN sign the winner INTO it using
          // the credential we already got from the popup (no second popup).
          // The winner still ends up verified with a real Google identity,
          // which is all the prize claim needs.
          if (code === "auth/credential-already-in-use" || code === "auth/email-already-in-use") {
            const cred = GoogleAuthProvider.credentialFromError(linkErr);
            if (!cred) throw linkErr;
            const signInResult = await signInWithCredential(auth, cred);
            linkedUid = signInResult.user?.uid;
          } else {
            throw linkErr; // cancelled / blocked / unsupported — handled below
          }
        }
        try {
          if (linkedUid) {
            // Stamp the Google email too — an anon-born doc has none, which
            // leaves the account unfindable by email (staff adds fail).
            const linkedEmail = (auth.currentUser?.email || "").trim().toLowerCase();
            await updateDoc(doc(db, "users", linkedUid), {
              anonymousSince: deleteField(),
              ...(linkedEmail ? { email: linkedEmail } : {}),
            });
          }
        } catch (_) {}
        try { clearAnonCredentials(); } catch (_) {}
      }
      const name = auth.currentUser?.displayName || "";
      const res = await claimPrize(
        DJChatId,
        c.prizeId,
        auth.currentUser.uid,
        name
      );
      if (res.result === "claimed") {
        try {
          await updateDoc(doc(db, "users", DJChatId, "djchats", c.djchatId), {
            prizeClaimed: true,
            prizeClaimedAt: serverTimestamp(),
          });
        } catch (_) {}
        // Save it to the guest's own prize archive, so the QR and code can be
        // brought back from their profile any time — not only from this modal,
        // which used to be the one and only place they ever existed.
        try {
          await recordPrizeWin(auth.currentUser.uid, {
            venueUid: DJChatId,
            venueName: c.venueName || clientObj?.muuzboxAccountName || "",
            djchatId: c.djchatId,
            prizeId: c.prizeId,
            prizeDescription: c.prizeDescription,
            trackTitle: c.trackTitle,
            trackArtist: c.trackArtist,
          });
        } catch (e) {
          console.warn("[Jukebox4] prize archive save failed:", e?.message);
        }
        setPrizeClaimStatus("claimed");
      } else if (res.result === "already_won") {
        // Someone linked & claimed first — this winner missed out. Clear the
        // winner flag on their request so the DJ isn't shown a phantom winner.
        try {
          await updateDoc(doc(db, "users", DJChatId, "djchats", c.djchatId), {
            isPrizeWinner: false,
            prizeMissed: true,
          });
        } catch (_) {}
        setPrizeClaimStatus("already_won");
      } else {
        setPrizeClaimStatus("error");
      }
    } catch (err) {
      const code = err?.code || "";
      console.warn("[Jukebox4] prize claim failed:", code, err?.message);
      if (code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request") {
        // The guest backed out of the Google popup — nothing forfeited, let
        // them try again from the prompt.
        setClaimError("");
        setPrizeClaimStatus("prompt");
      } else if (code === "auth/popup-blocked") {
        setClaimError("Your browser blocked the Google pop-up. Allow pop-ups for this site, then try again.");
        setPrizeClaimStatus("error");
      } else if (code === "auth/operation-not-supported-in-this-environment" || code === "auth/web-storage-unsupported") {
        setClaimError("This browser can't complete Google sign-in here. Open Muuzbox in your normal browser (not an in-app browser) and try again.");
        setPrizeClaimStatus("error");
      } else {
        setClaimError(err?.message || "Couldn't verify your Google account. Please try again.");
        setPrizeClaimStatus("error");
      }
    }
  };

  // Explicit decline → forfeit. Flags the request so the DJ desktop app is
  // notified; the prize itself stays active so it can be won by someone else.
  const doDeclinePrize = async () => {
    const c = prizeClaim;
    setPrizeClaimStatus("forfeited");
    if (c?.djchatId) {
      try {
        await updateDoc(doc(db, "users", DJChatId, "djchats", c.djchatId), {
          prizeForfeited: true,
          prizeForfeitedAt: serverTimestamp(),
        });
      } catch (_) {}
    }
  };
  const [displayNameOpen, setDisplayNameOpen] = useState(false);
  const [requestedTrack, setRequestedTrack] = useState({});
  const [pendingRequest, setPendingRequest] = useState(null); // Store request to process after profile update
  const [DJChatId, setDJChatId] = useState(clientObj.clientId); //("5fD03BaD7INyhJMne2gfLdPAKbQ2"); //clientObj

  // Channel Guest Hosting: when a guest DJ has pressed Go Live on this
  // VM host, `effectiveUid` is that DJ's uid; otherwise it's the VM's
  // own uid. Currently-playing / next-song reads follow effectiveUid so
  // audiences see the live DJ's actual set. Request writes still land on
  // the VM's queue (DJChatId), stamped with requestedForDjUid so the VM
  // admin can filter by DJ. See docs/channel-guests.md for the full model.
  const { liveGuestDj, guestDjs, effectiveUid } = useLiveGuestDj(clientObj.clientId);

  // Playlist tabs: which owner's playlist is the user *browsing* right
  // now. Defaults to the VM's own uid — when a guest DJ has Go Live'd,
  // the effective playing set comes from them and the tab strip auto-
  // switches to their tab. The user can freely browse any accepted
  // guest DJ's playlist by clicking their tab; requests are only
  // accepted from whichever tab is currently LIVE.
  const [activePlaylistOwnerUid, setActivePlaylistOwnerUid] = useState(clientObj.clientId);
  // Only accepted guests are eligible for tabs — pending / rejected
  // hosts are invisible to the guest audience.
  const acceptedGuestDjs = useMemo(
    () => (guestDjs || []).filter((g) => g.status === 'accepted'),
    [guestDjs]
  );
  // Auto-follow: if a guest DJ takes air, switch the active tab to
  // theirs so the guest sees the live playlist without having to click.
  // Suppressed once the user manually picks a different tab in this
  // session (tracked by hasManuallyPickedTabRef).
  const hasManuallyPickedTabRef = useRef(false);
  useEffect(() => {
    if (hasManuallyPickedTabRef.current) return;
    if (liveGuestDj && liveGuestDj !== activePlaylistOwnerUid) {
      setActivePlaylistOwnerUid(liveGuestDj);
    } else if (!liveGuestDj && activePlaylistOwnerUid !== clientObj.clientId) {
      // Guest went off air — fall back to the VM's own tab.
      setActivePlaylistOwnerUid(clientObj.clientId);
    }
  }, [liveGuestDj, clientObj.clientId, activePlaylistOwnerUid]);

  // canRequest — the LIVE tab is the only one that accepts submissions.
  // When nobody is live, the VM's tab is de-facto LIVE (its playlist is
  // what's actually playing). Any browsing on a non-live tab is
  // read-only preview.
  const canRequest = activePlaylistOwnerUid === (liveGuestDj || clientObj.clientId);

  // Effective clientObj — passed to any child that reads
  // currentSong/chat/mood off users/{clientId} so those reads follow
  // the live guest DJ when one has taken air. Falls back to the VM's
  // own clientObj shape when nobody is live (identity).
  const effectiveClientObj = useMemo(
    () => (effectiveUid && effectiveUid !== clientObj.clientId
      ? { ...clientObj, clientId: effectiveUid }
      : clientObj),
    [effectiveUid, clientObj]
  );
  const [requesteeInfo, setRequesteeInfo] = useState({});
  const [messages, setMessages] = useState([]);
  const [avatars, setAvatars] = useState({});
  // User selection dropdown states
  const [selectedRequestUser, setSelectedRequestUser] = useState(null);
  const [requestUserSearch, setRequestUserSearch] = useState("");
  const [showRequestUserDropdown, setShowRequestUserDropdown] = useState(false);
  const [filteredRequestUsers, setFilteredRequestUsers] = useState([]);
  const [allRequestUsers, setAllRequestUsers] = useState([]);
  const [groupedTracks, setGroupedTracks] = useState([]);
  const [newDisplayName, setNewDisplayName] = useState("");
  const [jukeboxUserObj, setJukeboxUserObj] = useState(userObj);
  const [jukeboxUserUPD, setJukeboxUserUPD] = useState([]);
  const [currentRequestDetails, setCurrentRequestDetails] = useState({});
  const [showUserModal, setShowUserModal] = useState(false);
  const [newPhotoURL, setNewPhotoURL] = useState("");
  const [animOne, setAnimOne] = useState(false);
  const [showServicesPanel, setShowServicesPanel] = useState(false);
  const [settings, setSettings] = useState({});
  const [currentSongId, setCurrentSongId] = useState(null);
  const [preventReTrigger, setPreventReTrigger] = useState(true);
  const [, updateState] = React.useState();
  const forceUpdate = React.useCallback(() => updateState({}), []);
  const [nextTrack, setNextTrack] = useState({});
  const [currentTrack, setCurrentTrack] = useState({});
  const [showNextTrackModal, setShowNextTrackModal] = useState(false);
  const [nextSongId, setNextSongId] = useState(null);
  const [nextSongObjectVotes, setNextSongObjectVotes] = useState(null);
  const [playlistId, setPlaylistId] = useState(null);
  const [currentPendingLength, setCurrentPendingLength] = useState(null);
  const [showCurrentTrackModal, setShowCurrentTrackModal] = useState(false);
  const [showDjModeModal, setShowDjModeModal] = useState(false);
  const [djModeShown, setDjModeShown] = useState(false);
  const [currentSongObj, setCurrentSongObj] = useState(null);
  const [requestModalTimer, setRequestModalTimer] = useState(null);
  const [isInChat2, setIsInChat2] = useState(null);
  const [chatId2, setChatId2] = useState(null);
  const [chatType, setChatType] = useState(null);
  const [trackDetails, setTrackDetails] = useState(null);
  const [chatUsers, setChatUsers] = useState([]);
  const [userObject, setUserObject] = useState({});
  //showChatlist
  const [showChat, setShowChat] = useState(showChatlist);
  const [showChat2, setShowChat2] = useState(finishChat);
  const [showJukeboxList, setShowJukeboxList] = useState(false);
  const isSearchActiveRef = useRef(false);
  const searchInputRef = useRef(null);
  // Voice search (Web Speech API)
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const recognitionRef = useRef(null);

  // YouTube search state
  const [videos, search] = useVideos('');
  // Tracks on the DJ's desktop local drive (published library index) that
  // match the guest's search but may not be in the loaded playlist. Shown
  // above the YouTube results.
  const [djLibraryResults, setDjLibraryResults] = useState([]);
  const [showYoutubeResults, setShowYoutubeResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [youtubeSearchHistory, setYoutubeSearchHistory] = useState([]);
  const youtubeDebounceTimeoutRef = useRef(null);
  const youtubeSearchTimeoutRef = useRef(null);

  // const [fileName, setFileName] = useState("");
  // // set initial state for the object
  // const [data, setData] = useState(null);
  // // call setfile on file input onChange

  // const [avatarImage, setAvatarImage] = useState("");
  // // State to store uploaded file
  // const [file, setFile] = useState("");

  // // progress
  // const [percent, setPercent] = useState(0);

  // // Handle file upload event and update state
  // function handleChange(event) {
  //   setFile(event.target.files[0]);
  // }

  // Navigate to playing tab when Apple Watch modal closes with selections
  useEffect(() => {
    if (navigateToPlaying) {
      setTab("playing");
      setNavigateToPlaying(false);
    }
  }, [navigateToPlaying, setNavigateToPlaying]);

  // Reset DJ mode modal when navigating between tabs
  useEffect(() => {
    // Reset DJ mode shown flag when navigating away from playing tab
    if (tab !== 'playing') {
      setDjModeShown(false);
      setShowDjModeModal(false);
    }
    // When returning to playing tab, check if we should show DJ mode modal
    else if (tab === 'playing' && currentSongId === 'dj-mode' && !djModeShown) {
      setShowDjModeModal(true);
      setDjModeShown(true);
    }
  }, [tab, currentSongId, djModeShown]);

  // Auto-close the request-confirmation modal and navigate to playing.
  const REQUEST_MODAL_AUTO_CLOSE_MS = 10000;
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        setIsOpen(false);
        setTab("playing");
        // Reset selections
        setSelectedRequestUser(null);
        setRequestUserSearch("");
        setShowRequestUserDropdown(false);
      }, REQUEST_MODAL_AUTO_CLOSE_MS);

      return () => {
        // Cleanup timer if the modal is closed manually before it fires.
        clearTimeout(timer);
      };
    }
  }, [isOpen]);

  // Live points total (for the request-confirmation modal's progress bar).
  useEffect(() => {
    if (!auth.currentUser?.uid) return undefined;
    const unsub = subscribePoints(auth.currentUser.uid, setPoints);
    return () => unsub && unsub();
  }, []);

  // When the confirmation modal opens, fill the tier-progress bar 2s later with
  // a light haptic tap. Reset to 0 on each open so it always animates up.
  useEffect(() => {
    if (!isOpen) { setAnimateProgress(false); setShiver(false); return undefined; }
    setAnimateProgress(false);
    setShiver(false);
    const timers = [];
    timers.push(setTimeout(() => {
      setAnimateProgress(true);
      // Strong ~1.4s buzz (three pulses) so it's really felt — Android/Chromium
      // only; a silent no-op on iOS.
      try { if (navigator.vibrate) navigator.vibrate([400, 100, 400, 100, 400]); } catch (_) {}
      // Visual equivalent for iOS (and everyone): a rapid half-second shiver.
      setShiver(true);
      timers.push(setTimeout(() => setShiver(false), 500));
    }, 2000));
    return () => timers.forEach(clearTimeout);
  }, [isOpen]);

  // Fetch all users for dropdown selection - only when dropdown is open
  useEffect(() => {
    // Only fetch when dropdown is actually open
    if (!showRequestUserDropdown) {
      return;
    }

    const usersRef = collection(db, "users");
    const q = query(
      usersRef,
      where("isAnonymous", "==", false),
      limit(100) // Limit to 100 users
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const users = snapshot.docs.map((doc) => {
        const userData = doc.data();
        return {
          id: doc.id,
          displayName: userData.displayName || "",
          email: userData.email || "",
          photoURL: userData.photoURL || null
        };
      });
      setAllRequestUsers(users);
    });

    return () => unsubscribe();
  }, [showRequestUserDropdown]);

  // Filter users based on search input
  useEffect(() => {
    if (requestUserSearch.trim()) {
      const searchLower = requestUserSearch.toLowerCase().trim();
      const filtered = allRequestUsers.filter((user) => {
        const displayName = (user.displayName || "").toLowerCase();
        const email = (user.email || "").toLowerCase();
        return displayName.includes(searchLower) || email.includes(searchLower);
      });
      setFilteredRequestUsers(filtered);
      setShowRequestUserDropdown(filtered.length > 0);
    } else {
      setFilteredRequestUsers([]);
      setShowRequestUserDropdown(false);
    }
  }, [requestUserSearch, allRequestUsers]);

  useEffect(() => {
    setAnimOne(true);
    const timer1 = setTimeout(() => {
      setShowServicesPanel(false);
    }, 500);
    const timer2 = setTimeout(() => {
      setAnimOne(false);
    }, 1000);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, [openJukeboxPlaylist]);

  useEffect(() => {
    // setIsInChat2(!showChat);
    // console.log('showChat', showChat2);
    // console.log("isInChat2", isInChat2);
    // console.log("finishChat", finishChat);
    if (finishChat && isInChat2) {
      setIsInChat2(false);
    }
  }, [finishChat, isInChat2]);

  useEffect(() => {
    const playerControlData = async () => {
      if (message) {
        const data = JSON.parse(message);
        if (data.viewNextSong) {
          // console.log("data.viewNextSong", data.viewNextSong);
          // viewNextTrack();

          const idx = jukebox.findIndex((item) => item.id === currentSongId);
          if (idx !== -1) {
            // console.log("current jukebox track and index", jukebox[idx], idx);
            // console.log("next song id is", jukebox[idx + 1]);
            setNextTrack(jukebox[idx + 1]);
            setShowNextTrackModal(true);
          }
        }
        // Open a specific chat on request from another panel (e.g. the Users
        // panel tapping a person). Switches to the chat tab and opens the
        // conversation window directly — same path as tapping an existing chat.
        if (data.openChat && data.openChat.chatId) {
          setTab("chat");
          pickedChat(
            data.openChat.chatId,
            "private",
            null,
            data.openChat.users || []
          );
        }
      }
    };
    playerControlData();
  }, [message]);

  useEffect(() => {
    const initAutoplay = async () => {
      // getSiteConfig();
      const queryMessages = query(
        userJukeboxRef /* jukeboxRef ,
      where("room", "==", room),
      orderBy("createdAt")*/
      );

      let jukeboxList = [];
      const unsuscribe = onSnapshot(queryMessages, (snapshot) => {
        jukeboxList = [];
        snapshot.forEach((doc) => {
          jukeboxList.push({ ...doc.data(), id: doc.id });
        });
      });

      let settingsObj = {};
      const AutoplaySettingsRef = doc(db, "users", DJChatId);
      const docSnap = await getDoc(AutoplaySettingsRef);
      if (docSnap.exists()) {
        // eslint-disable-next-line no-const-assign
        settingsObj = {
          userRequestRate: parseInt(docSnap.data().userRequestRate),
          songRate: parseInt(docSnap.data().songRate),
        };
      }
      // console.log("settingsObj", settingsObj);
      setSettings(settingsObj);

      const pendingRef = collection(
        db,
        `users`,
        clientObj.clientId,
        "pendingList"
      );

      let pl = [];
      const unsuscribe1 = onSnapshot(pendingRef, (snapshot) => {
        pl = [];
        snapshot.forEach((doc) => {
          // console.log('doc.data().muuzboxCurrentSong', doc.data().muuzboxCurrentSong.id)
          const data = doc.data();
          data.rand = Math.floor(Math.random() * jukeboxList.length);
          pl.push(data);
        });
        const pendingListUpdate = { pendingListUpdate: JSON.stringify(pl) };
        setMessage(JSON.stringify(pendingListUpdate));
      });

      // Read currentSong from the *effective* uid — the live guest DJ
      // when one has pressed Go Live on this VM, otherwise the VM itself.
      // Falls back to clientObj.clientId if the hook hasn't hydrated yet.
      const currentSongOwnerUid = effectiveUid || clientObj.clientId;
      const currentSongRef = collection(
        db,
        "users",
        currentSongOwnerUid,
        "currentSong"
      );

      let dj = [];
      const unsuscribe2 = onSnapshot(currentSongRef, (snapshot) => {
        dj = [];
        snapshot.forEach((doc) => {
          // console.log('doc.data().muuzboxCurrentSong', doc.data().muuzboxCurrentSong.id)
          dj.push(doc.data().muuzboxCurrentSong);
          const songId = doc.data().muuzboxCurrentSong.id;
          setCurrentSongId(songId);

          // Check if entering DJ mode and we haven't shown modal yet
          if (songId === 'dj-mode' && !djModeShown && tab === 'playing') {
            setShowDjModeModal(true);
            setDjModeShown(true);
          }
          // Reset when leaving DJ mode
          if (songId !== 'dj-mode') {
            setDjModeShown(false);
            setShowDjModeModal(false);
          }

          // NOTE: playlistId is no longer set from *this* listener. It's
          // driven by a dedicated effect below that follows
          // activePlaylistOwnerUid, so browsing another DJ's tab loads
          // that DJ's playlist without disturbing what's actually playing.
          // console.log('plid', plid);
        });
        // Update currentSongObj when currentSong document changes
        setCurrentSongObj(dj[0]);
      });

      // Same effective-uid reroute as currentSong above.
      const nextSongRef = collection(
        db,
        "users",
        currentSongOwnerUid,
        "nextSong"
      );

      let ns = [];

      const unsuscribe3 = onSnapshot(nextSongRef, (snapshot) => {
        ns = [];
        snapshot.forEach((doc) => {
          ns.push(doc.data().muuzboxNextSong);
          setNextSongId(doc.data().muuzboxNextSong.id);
        });
        //console.log('next song', ns);
        // send updated next song to the next song component
        const newtrackupdate = { newtrackupdate: JSON.stringify(ns[0]) };
        setMessage(JSON.stringify(newtrackupdate));
        // Update nextTrack state so NowPlaying receives the updated data via props
        if (ns[0]) {
          setNextTrack(ns[0]);
        }
      });

      return () => {
        unsuscribe();
        unsuscribe1();
        unsuscribe2();
        unsuscribe3();
      };
    };
    initAutoplay();
    // effectiveUid is included so the currentSong/nextSong listeners
    // resubscribe to the live guest DJ's docs the moment Go Live flips
    // (and back to the VM when they Go Off Air). Without it the initial
    // subscription would stay stuck on whichever uid was resolved at mount.
  }, [DJChatId, forceUpdate, effectiveUid]);

  // Playlist source follower — separate from the currentSong listener so
  // browsing a different DJ's tab loads their playlist without touching
  // what's actually playing. Reads `users/{activePlaylistOwnerUid}/currentSong`
  // and lifts the .playlistId onto local state; the tracks effect
  // downstream keys off playlistId and re-fetches on change.
  useEffect(() => {
    if (!activePlaylistOwnerUid) return undefined;
    const currentSongRef = collection(db, "users", activePlaylistOwnerUid, "currentSong");
    const unsub = onSnapshot(currentSongRef, (snapshot) => {
      let nextPlaylistId = null;
      snapshot.forEach((d) => {
        const song = d.data() && d.data().muuzboxCurrentSong;
        if (song && song.playlistId) nextPlaylistId = song.playlistId;
      });
      if (nextPlaylistId) setPlaylistId(nextPlaylistId);
    });
    return () => unsub();
  }, [activePlaylistOwnerUid]);

  // Separate useEffect for tracks listener based on playlistId
  useEffect(() => {
    if (!playlistId) {
      return;
    }

    const listRef = collection(db, "playlists", playlistId, "tracks");
    const queryTracks = query(listRef, orderBy("ordinal"));

    const unsubscribe = onSnapshot(queryTracks, (snapshot) => {
      const playlist = snapshot.docs.map((docItem) => {
        const data = docItem.data();
        // Use YouTube thumbnail as fallback if coverart is missing
        const youtubeThumbnail = data.youTubeId
          ? `https://img.youtube.com/vi/${data.youTubeId}/mqdefault.jpg`
          : "";

        const track = {
          id: docItem.id,
          track: data.track,
          genre: data.genre,
          artist: data.artist,
          coverart: data.coverart || data.coverart2 || youtubeThumbnail || FALLBACK_COVERART,
          coverart2: data.coverart2 || "",
          url: data.url,
          youTubeId: data.youTubeId,
        };

        if (docItem.id === currentSongId) {
          track.currentSong = "true";
        }

        return track;
      });

      // Always update the source of truth
      setOriginalJukebox(playlist);
      setGroupedTracks(groupTracksByGenre(playlist));
      setTracks(playlist);
      // Only update jukebox if user is not actively searching
      if (!isSearchActiveRef.current) {
        setJukebox(playlist);
      }
    });

    return () => unsubscribe();
  }, [playlistId, currentSongId]);

  useEffect(() => {
    setJukeboxUserObj(userObj);
  }, [userObj]);


  const handleProfileUpdate = (updatedUserData) => {
    // Update local jukeboxUserObj state with the fresh data from Firebase
    setJukeboxUserObj(updatedUserData);

    // If there's a pending request, process it now that the profile is updated
    if (pendingRequest && updatedUserData.nameChanged) {
      sendThisPickToDJ(pendingRequest);
      setPendingRequest(null); // Clear the pending request
      // Don't navigate - user stays on current tab (request page)
    }
  };

  const handleQuickProfileSave = async () => {
    try {
      if (!newDisplayName || newDisplayName.trim() === "") {
        alert("Please enter a display name");
        return;
      }

      const ref = doc(db, `users`, auth.currentUser.uid);
      await updateDoc(ref, {
        displayName: newDisplayName.trim(),
        nameChanged: true,
        updatedAt: serverTimestamp(),
      });

      // Reload the user data from Firebase
      const updatedUserDoc = await getDoc(ref);
      if (updatedUserDoc.exists()) {
        const updatedUserData = updatedUserDoc.data();
        const updatedUserObj = {
          ...jukeboxUserObj,
          ...updatedUserData,
          id: updatedUserDoc.id
        };

        // Update local state
        setJukeboxUserObj(updatedUserObj);

        // Close the modal
        setDisplayNameOpen(false);

        // Process the pending request
        if (pendingRequest) {
          sendThisPickToDJ(pendingRequest);
          setPendingRequest(null);
        }
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      alert("Failed to update profile. Please try again.");
    }
  };

  useEffect(() => {
    const chatsMessgesRef = collection(
      db,
      "users",
      DJChatId,
      "djchats",
      DJChatId,
      "messages"
    );
    const queryMessages = query(chatsMessgesRef, orderBy("createdAt"));

    // Cache to avoid refetching user data
    const userCache = {};

    const unsuscribe = onSnapshot(queryMessages, async (snapshot) => {
      let messagesx = [];
      let avatars = [];

      snapshot.forEach((doc) => {
        // create an avatar list that can be referred to when the chatlist is rendered
        avatars.push(doc.data().uid);
        messagesx.push(Object.assign({ ...doc.data(), id: doc.id }));
      });

      // Get unique user IDs
      const uniq = [...new Set(avatars)];
      const avatarObj = {};

      // Fetch only users not in cache
      const fetchPromises = uniq.map(async (uid) => {
        // Check cache first
        if (userCache[uid]) {
          avatarObj[uid] = userCache[uid];
          return;
        }

        // Fetch from Firebase if not cached
        try {
          const docRef = doc(db, "users", uid);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const userData = {
              displayName: docSnap.data().displayName,
              photoURL: docSnap.data().photoURL,
              email: docSnap.data().email,
            };
            avatarObj[uid] = userData;
            userCache[uid] = userData; // Store in cache
          } else {
            // Fallback for non-existent users
            const fallbackData = {
              displayName: "",
              photoURL: "",
            };
            avatarObj[uid] = fallbackData;
            userCache[uid] = fallbackData; // Cache fallback
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
        }
      });

      // Wait for all fetches to complete
      await Promise.all(fetchPromises);

      // Update state with fetched data
      setAvatars(avatarObj);
      setMessages(messagesx);
    });

    return () => unsuscribe();
  }, [DJChatId]);
  

  const checkUserPickInList = async (item) => {
    // console.log("checkUserPickInList", item.uid);
    // check if user has already picked a song in the last 20 minutes
    const requestFeedbackRef = collection(db, "users", DJChatId, "djchats");
    const queryMessages = query(
      requestFeedbackRef,
      where("uid", "==", item.uid),
      orderBy("createdAt")
    );

    // Use getDocs for one-time read instead of onSnapshot
    try {
      const snapshot = await getDocs(queryMessages);
      let requestFeedback = [];
      snapshot.forEach((doc) => {
        const thisItemtimeStamp = doc.data().createdAt;
        const timestampInSeconds = (Date.now() / 1000) | 0;
        if (timestampInSeconds - thisItemtimeStamp.seconds < 1200) {
          // console.log(
          //   "you have already requested a song in the last 20 minutes"
          // );
        } else {
          requestFeedback.push({ ...doc.data(), id: doc.id });
        }
      });
      return requestFeedback;
    } catch (error) {
      console.error("Error checking user pick in list:", error);
      return [];
    }
  };

  const arraySearch = (array, keyword) => {
    //console.log('keyword', keyword);
    //console.log('array', array )

    // Helper function to normalize text by removing apostrophes and commas
    const normalizeText = (text) => {
      return text.toLowerCase().replace(/[',]/g, "");
    };

    const searchTerm = normalizeText(keyword);
    //console.log('searchTerm', searchTerm);
    return array.filter((value) => {
      if (!value.track) value.track = value.artist;
      if (!value.artist) value.artist = value.track;
      if (!value.artist && !value.track) return null;
      //console.log('value', value);

      // Normalize track and artist names for comparison
      const normalizedTrack = normalizeText(value.track);
      const normalizedArtist = normalizeText(value.artist);

      //console.log('normalizedTrack.match(new RegExp(searchTerm, "g"))', normalizedTrack.match(new RegExp(searchTerm, "g")));
      //console.log('normalizedArtist.match(new RegExp(searchTerm, "g"))', normalizedArtist.match(new RegExp(searchTerm, "g")));

      return (
        normalizedTrack.match(new RegExp(searchTerm, "g")) ||
        normalizedArtist.match(new RegExp(searchTerm, "g"))
      );
    });
  };

  const resetSearch = () => {
    isSearchActiveRef.current = false;
    setShowJukeboxList(false);
    setJukebox(originalJukebox);
    setCount(originalJukebox.length);
    setShowYoutubeResults(false);
    setIsSearching(false);
    setDjLibraryResults([]);
    if (youtubeDebounceTimeoutRef.current) {
      clearTimeout(youtubeDebounceTimeoutRef.current);
    }
    if (youtubeSearchTimeoutRef.current) {
      clearTimeout(youtubeSearchTimeoutRef.current);
    }
    if (searchInputRef.current) {
      searchInputRef.current.value = '';
    }
  };

  const handleSubmit = async (event) => {
    let value = event.target.value;
    if (value.length > 2) {
      isSearchActiveRef.current = true;
      setShowJukeboxList(true);
      let resultSearch = await arraySearch(originalJukebox, value);
      setJukebox(resultSearch);
      setCount(resultSearch.length);

      // DJ library search — tracks on the desktop's local drive that aren't
      // in the loaded playlist. Index is cached after first fetch, so this is
      // cheap per keystroke. Playlist matches are excluded (they already show
      // in the genre sections above).
      searchDjLibrary(clientObj?.clientId, value)
        .then((hits) => {
          if (!isSearchActiveRef.current) return;
          const inPlaylist = new Set((originalJukebox || []).map((j) => j.id));
          setDjLibraryResults(hits.filter((h) => h.id && !inPlaylist.has(h.id)));
        })
        .catch(() => setDjLibraryResults([]));

      // Trigger YouTube search if 7+ characters typed
      if (value.length >= 7) {
        setShowYoutubeResults(true);
        setIsSearching(true);

        if (youtubeDebounceTimeoutRef.current) {
          clearTimeout(youtubeDebounceTimeoutRef.current);
        }
        if (youtubeSearchTimeoutRef.current) {
          clearTimeout(youtubeSearchTimeoutRef.current);
        }

        // Debounce: wait 800ms after user stops typing
        youtubeDebounceTimeoutRef.current = setTimeout(() => {
          const now = Date.now();
          const recentSearches = youtubeSearchHistory.filter(t => t > now - 60000);

          if (recentSearches.length >= 3) {
            const oldestTime = Math.min(...recentSearches);
            const wait = (oldestTime + 60000) - now;
            youtubeSearchTimeoutRef.current = setTimeout(() => {
              const updated = youtubeSearchHistory.filter(t => t > Date.now() - 60000);
              setYoutubeSearchHistory([...updated, Date.now()]);
              search(value);
              setIsSearching(false);
            }, wait);
          } else {
            setYoutubeSearchHistory([...recentSearches, now]);
            search(value);
            setIsSearching(false);
          }
        }, 800);
      } else {
        setShowYoutubeResults(false);
        setIsSearching(false);
        if (youtubeDebounceTimeoutRef.current) {
          clearTimeout(youtubeDebounceTimeoutRef.current);
        }
        if (youtubeSearchTimeoutRef.current) {
          clearTimeout(youtubeSearchTimeoutRef.current);
        }
      }
    } else {
      isSearchActiveRef.current = false;
      setShowJukeboxList(false);
      setJukebox(originalJukebox);
      setCount(originalJukebox.length);
      setShowYoutubeResults(false);
      setIsSearching(false);
      setDjLibraryResults([]);
    }
  };

  // ---- Voice search: speak a request straight into the search box ----
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SR) setSpeechSupported(true);
    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.onend = null;
          recognitionRef.current.stop();
        } catch (e) {}
      }
    };
  }, []);

  const toggleListening = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;

    // Already listening -> stop and let onend run the final search
    if (isListening && recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {}
      return;
    }

    const recognition = new SR();
    recognition.lang = navigator.language || "en-US";
    recognition.interimResults = true;
    recognition.continuous = false;
    recognition.maxAlternatives = 1;
    recognitionRef.current = recognition;

    recognition.onstart = () => {
      setInputFocus(true);
      setIsListening(true);
    };

    recognition.onresult = (event) => {
      let transcript = "";
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      transcript = transcript.trim();
      if (searchInputRef.current) searchInputRef.current.value = transcript;
      // Live-update results as words come in (YouTube call is debounced in handleSubmit)
      handleSubmit({ target: { value: transcript } });
    };

    recognition.onerror = () => setIsListening(false);

    recognition.onend = () => {
      setIsListening(false);
      const finalValue = searchInputRef.current
        ? searchInputRef.current.value.trim()
        : "";
      if (finalValue.length > 2) handleSubmit({ target: { value: finalValue } });
    };

    try {
      recognition.start();
    } catch (e) {
      setIsListening(false);
    }
  };

  const handleSubmitDisplayname = async () => {
    if (newDisplayName.length > 2 || newDisplayName.length === 0) {
      if (newDisplayName.length === 0) {
        setNewDisplayName(userObj.displayName);
      }
      const ref = doc(db, `users`, auth.currentUser.uid);
      updateDoc(ref, {
        displayName: newDisplayName,
        nameChanged: true,
      });
      const newObj = jukeboxUserObj;
      newObj.displayName = newDisplayName;
      newObj.nameChanged = true;
      setJukeboxUserObj(newObj);
      setDisplayNameOpen(false);
      setIsOpen(!isOpen);
      // now send this request to dj
      sendThisPickToDJ(currentRequestDetails);
    } else {
      alert("new name needs to be longer");
    }
  };

  const handleUpdateDisplayname = async (event) => {
    let value = event.target.value;
    if (value.length > 2) {
      setNewDisplayName(value);
    }
  };

  // Handle user selection from dropdown
  const handleSelectRequestUser = async (user) => {
    setSelectedRequestUser(user);
    setRequestUserSearch("");
    setShowRequestUserDropdown(false);
    
    // Update the request in djchats with the selected user
    if (requesteeInfo.DJChatID) {
      try {
        const requestRef = doc(db, "users", DJChatId, "djchats", requesteeInfo.DJChatID);
        const requestDoc = await getDoc(requestRef);
        
        if (requestDoc.exists()) {
          const currentData = requestDoc.data();
          // Update the request object with requestee field
          await updateDoc(requestRef, {
            request: {
              ...currentData.request,
              requestee: {
                id: user.id,
                displayName: user.displayName,
                photoURL: user.photoURL,
                email: user.email
              }
            }
          });
          alert("Request updated with requestee: "+ user.displayName);
          // console.log("requestRef id: "+ requestRef.id);
          alert("DJChat id: "+ requesteeInfo.DJChatID);
            
        }
      } catch (error) {
        console.error("Error updating request with requestee:", error);
      }
    }
  };

  const pickTrack = async (event, item, i) => {
    event.preventDefault();
    resetSearch();
    // Channel Guest Hosting: only the LIVE tab accepts requests. The
    // pickTrack button is still shown on non-live tabs so the guest can
    // browse another DJ's set, but pressing it is a no-op that surfaces
    // a nudge to switch tabs. The banner above the tab strip explains
    // the same rule; this is the belt-and-braces catch for any code
    // path that reaches pickTrack directly (search results, YT clicks).
    if (!canRequest) {
      try {
        setMessage(JSON.stringify({
          toast: 'Only the LIVE DJ takes requests. Tap the LIVE tab to submit.',
        }));
      } catch (_) {}
      return;
    }
    if (isOpen) {
      setIsOpen(!isOpen);
    } else {
      if (auth.currentUser.isAnonymous) {
        setIsOpen(false);
        if (!jukeboxUserObj.nameChanged) {
          // Store the pending request and show profile modal on request page
          setPendingRequest(item);
          setDisplayNameOpen(true);
        } else {
          setIsOpen(!isOpen);
          sendThisPickToDJ(item);
        }
        // lets check what the user name is
        setCurrentRequestDetails(item);
      } else {
        setIsOpen(!isOpen);
        // console.log("sending this track", item);
        sendThisPickToDJ(item);
      }
    }
  };

  const pickedChat = (chatId, type, trackDetails, users) => {
    // closeAllPanels();
    setIsInChat2(true);
    // setChatId(chatId);
    setChatId2(null);
    setChatId2(chatId);
    setChatType(type);
    setTrackDetails(trackDetails);
    setChatUsers(users);
    // console.log("chatScreen", users);
    chatScreen(users, chatId);
  };

  const getCurrentDisplayName = async () => {
    try {
      const requestCurrentIdRef = doc(db, "users", auth.currentUser.uid);
      const user = await getDoc(requestCurrentIdRef, requestCurrentIdRef);
      return user.displayName;
    } catch (err) {
      console.error("error:", err);
    }
  };

  // defaul settings for track/user frequency. clients can change the settings according to their taste
  const userchecked = async () => {
    // Check if user has exceeded their hourly request quota
    // settings.userRequestRate defines max requests per hour
    
    try {
      // Validate settings are loaded
      if (!settings || typeof settings.userRequestRate !== 'number') {
        console.warn("userchecked: Settings not loaded, allowing request");
        return true; // Permissive default if settings not ready
      }

      // Get user's requests from the past hour
      const requestsRef = collection(db, "users", auth.currentUser.uid, "requests");
      const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000); // 60 minutes ago
      
      // Query requests within the last hour
      const recentRequestsQuery = query(
        requestsRef,
        where("createdAt", ">", oneHourAgo),
        orderBy("createdAt", "desc")
      );
      
      const snapshot = await getDocs(recentRequestsQuery);
      const requestCount = snapshot.size;
      const maxRequests = settings.userRequestRate;
      
      // Check if user has exceeded the hourly limit
      if (requestCount >= maxRequests) {
        // Find the oldest request in the hour window to calculate wait time
        const requests = [];
        snapshot.forEach(doc => {
          const data = doc.data();
          if (data.createdAt) {
            requests.push(data.createdAt.toMillis());
          }
        });
        
        if (requests.length > 0) {
          // Sort to find the oldest request that would need to expire
          requests.sort((a, b) => a - b);
          const oldestRelevantRequest = requests[requests.length - maxRequests];
          const waitTimeMs = (oldestRelevantRequest + 60 * 60 * 1000) - Date.now();
          const waitTimeMinutes = Math.ceil(waitTimeMs / 60000);
          
          if (waitTimeMinutes > 0) {
            alert(`You've reached your limit of ${maxRequests} requests per hour. Please wait ${waitTimeMinutes} minute${waitTimeMinutes > 1 ? 's' : ''} before making another request.`);
            return false;
          }
        }
      }
      
      // User is within their limit
      const remainingRequests = maxRequests - requestCount;
      // console.log(`User has ${remainingRequests} request${remainingRequests !== 1 ? 's' : ''} remaining this hour`);
      return true;
      
    } catch (err) {
      console.error("userchecked error:", err);
      // Return permissive default on error to avoid blocking user
      return true;
    }
  };

  const trackFreqChecked = async (item) => {
    // Check if track is already in pending list or if enough time has passed since last request
    // settings.songRate defines the cooldown period in minutes
    // e.g., songRate = 2 means track can be requested again after 2 minutes
    // e.g., songRate = 30 means track can be requested again after 30 minutes

    try {
      // First check if track is already in the pending list
      const pendingRef = collection(db, "users", DJChatId, "pendingList");
      const pendingSnapshot = await getDocs(pendingRef);

      let isInPendingList = false;
      pendingSnapshot.forEach((doc) => {
        const data = doc.data();
        // Check if the track ID matches (assuming pending items have a track or request object with an id)
        if (data.request && data.request.id === item.id) {
          isInPendingList = true;
        } else if (data.trackId === item.id) {
          isInPendingList = true;
        } else if (data.id === item.id) {
          isInPendingList = true;
        }
      });

      if (isInPendingList) {
        console.log('[songrate] Track request denied: already in pending list');
        alert('This track is already in the queue. Please wait for it to play or choose a different track.');
        return false;
      }

      // Validate settings are loaded
      if (!settings || typeof settings.songRate !== 'number') {
        console.warn("trackFreqChecked: Settings not loaded, allowing request");
        return true; // Permissive default if settings not ready
      }

      // Check cooldown period
      const requestTrackFreqRef = doc(db, "users", DJChatId, "djtracks", item.id);
      const track = await getDoc(requestTrackFreqRef);

      if (track.exists()) {
        const data = track.data();
        if (data.lastRequestedAt) {
          const lastRequestTimestamp = data.lastRequestedAt.toMillis();
          const rightNow = Date.now();
          const cooldownMs = settings.songRate * 60000; // Convert minutes to milliseconds
          const timeSinceLastRequest = rightNow - lastRequestTimestamp;

          console.log('[songrate] Last request:', new Date(lastRequestTimestamp).toLocaleTimeString());
          console.log('[songrate] Current time:', new Date(rightNow).toLocaleTimeString());
          console.log('[songrate] Cooldown period:', settings.songRate, 'minutes');
          console.log('[songrate] Time since last request:', Math.floor(timeSinceLastRequest / 1000), 'seconds');

          if (timeSinceLastRequest < cooldownMs) {
            // Not enough time has passed
            const remainingMs = cooldownMs - timeSinceLastRequest;
            const remainingMinutes = Math.ceil(remainingMs / 60000);
            const remainingSeconds = Math.ceil(remainingMs / 1000) % 60;

            console.log('[songrate] Track request denied: cooldown active');

            if (remainingMinutes > 1) {
              alert(`This track is on cooldown. Please wait ${remainingMinutes} minutes before requesting it again.`);
            } else {
              alert(`This track is on cooldown. Please wait ${remainingSeconds} seconds before requesting it again.`);
            }
            return false;
          } else {
            console.log('[songrate] Track request allowed: cooldown expired');
            return true;
          }
        } else {
          // No previous request timestamp, allow the request
          console.log('[songrate] Track request allowed: never requested before');
          return true;
        }
      } else {
        // Track document doesn't exist, this is the first request
        console.log('[songrate] Track request allowed: first time request');
        return true;
      }
    } catch (err) {
      console.error("trackFreqChecked error:", err);
      // Return permissive default on error to avoid blocking user
      return true;
    }
  };

  const processRequest = (item) => {
    let prepareOpenRequest = new Promise(function (resolve, reject) {
      resolve("Promise resolved");
    });

    prepareOpenRequest
      .then(async () => {
        try {
          const requestCurrentIdRef = doc(db, "users", auth.currentUser.uid);
          const user = await getDoc(requestCurrentIdRef);
          if (user.exists()) {
            const displayName = user.data().displayName;
            return displayName;
          }
        } catch (err) {
          console.error("error:", err);
        }
      })
      .then(async (displayName) => {
        const requestSongRef4 = collection(db, "users", DJChatId, "djchats");
        // console.log("displayName", displayName);
        if (newDisplayName !== "") displayName = newDisplayName;

        // Prize-draw check: if this DJ has an active prize (or any of multiple
        // prizes) and the requested track matches one of them, persist the
        // flag + winningPrizeId on the chat doc + trigger the modal showing
        // THAT specific prize's description.
        let winningPrize = null;
        try {
          const prizeDoc = await getCurrentPrize(DJChatId);
          winningPrize = findWinningPrize(item, prizeDoc);
        } catch (err) {
          console.warn('[Jukebox4] Prize check failed:', err);
        }

        try {
          // console.log("item to add to requests", item);
          // Channel Guest Hosting: stamp the request with whichever DJ
          // is currently live on this VM host (or the VM's own uid when
          // no guest is live). Lets the VM admin filter the request feed
          // by DJ, and gives the analytics dashboards a way to attribute
          // request volume to the guest who was on air.
          const requestedForDjUid = liveGuestDj || clientObj.clientId;
          const newprod = await addDoc(requestSongRef4, {
            request: { ...item, youTubeId: item.youTubeId || "" },
            createdAt: serverTimestamp(),
            user: displayName || "",
            uid: auth.currentUser.uid,
            requestedForDjUid,
            ...(winningPrize ? {
              isPrizeWinner: true,
              winningPrizeId: winningPrize.id || null,
              winningPrizeDescription: winningPrize.prizeDescription || '',
            } : {}),
          });
          const requesteeObj = {DJChatID: newprod.id}
          setRequesteeInfo(requesteeObj);

          // Flag this track so NowPlaying scrolls to + flashes it and floats a
          // vote reminder once the guest is taken to the Now Playing page.
          setPendingHighlight({
            trackId: item.id || item.youTubeId || null,
            title: item.title || item.track || "",
            artist: item.artist || "",
            nonce: Date.now(),
          });

          if (winningPrize) {
            setPrizeWinDetails({
              trackTitle: item.title || item.track || winningPrize.trackTitle,
              trackArtist: item.artist || winningPrize.trackArtist,
              prizeDescription: winningPrize.prizeDescription || '',
            });
            // Is this winner an unlinked guest? Firestore anonymousSince is the
            // cross-origin source of truth (isAnonymous flips false when bridged
            // via signInWithCustomToken from another origin).
            let isGuest = !!auth.currentUser?.isAnonymous;
            if (!isGuest) {
              try {
                const uSnap = await getDoc(doc(db, "users", auth.currentUser.uid));
                isGuest = !!uSnap.data()?.anonymousSince;
              } catch (_) {}
            }
            const claim = {
              djchatId: newprod.id,
              prizeId: winningPrize.id || "legacy",
              isAnonymous: isGuest,
              // Carried IN the claim, not read from prizeWinDetails: for an
              // already-linked winner doClaimPrize runs in this same tick,
              // before that setState lands, and would save an empty prize.
              prizeDescription: winningPrize.prizeDescription || "",
              trackTitle: item.title || item.track || winningPrize.trackTitle || "",
              trackArtist: item.artist || winningPrize.trackArtist || "",
              venueName: clientObj?.muuzboxAccountName || "",
            };
            setPrizeClaim(claim);
            setClaimError("");
            setPrizeClaimStatus("prompt");
            setShowPrizeWinner(true);
            // Already-linked winners have nothing to attach — claim right away.
            if (!isGuest) doClaimPrize(claim);
          }

          // After every successful song request from an anonymous guest,
          // bump the per-uid counter and (after the 2nd request) fire the
          // "Save your request history → Continue with Google" prompt.
          // Non-anonymous users are ignored inside the helper.
          try {
            recordRequestAndMaybePrompt(
              auth.currentUser?.uid,
              !!auth.currentUser?.isAnonymous
            );
          } catch (_) {}

          // Loyalty points: +1 for a song request (fire-and-forget; never blocks).
          awardRequestPoint({
            uid: auth.currentUser?.uid,
            venueUid: clientObj.clientId,
            trackId: item.id || item.youTubeId || null,
          }).catch(() => {});

          // Backfill the real genre (guessGenre Cloud Function). Fire-and-forget:
          // the request is already saved; genre lands on the doc a moment later.
          backfillGenre({
            title: item.track || item.title,
            artist: item.artist,
            currentGenre: item.genre,
            targets: [
              { ref: doc(db, "users", DJChatId, "djchats", newprod.id), field: "request.genre" },
            ],
          }).catch(() => {});

          return newprod;
        } catch (error) {
          console.error("Error adding document: ", error);
        }
      })
      .then(async (newprod) => {
        const requestSongRef5 = collection(
          db,
          "users",
          DJChatId,
          "djchats",
          newprod.id,
          "requesters"
        );
        try {
          await addDoc(requestSongRef5, {
            createdAt: serverTimestamp(),
            user: auth.currentUser.displayName || auth.currentUser.email,
            uid: auth.currentUser.uid,
          });
          return newprod;
        } catch (error) {
          console.error("Error adding document: ", error);
        }
      })
      .then(async (newprod) => {
        const requestSongRef6 = collection(
          db,
          "users",
          auth.currentUser.uid,
          "requests"
        );
        try {// needs to be updated to include the DJChatId as the client user id
          await addDoc(requestSongRef6, {
            createdAt: serverTimestamp(),
            djchatId: newprod.id,
            clientId: DJChatId,
            channel: userObj.channel,
          });
        } catch (error) {
          console.error("Error adding document: ", error);
        }
      })
      .then(async () => {
        try {
          const update = { lastMuuzboxRequest: serverTimestamp() };
          await setDoc(
            doc(db, "users", auth.currentUser.uid),
            {
              ...update,
            },
            { merge: true }
          );
        } catch (error) {
          console.error("Error updating user document: ", error);
        }
      })
      .then(async () => {
        try {
          // console.log('updating djtracks document', item.id)
          const update = {
            lastRequestedAt: serverTimestamp(),
            uid: auth.currentUser.uid,
          };
          await setDoc(
            doc(db, "users", DJChatId, "djtracks", item.id),
            {
              ...update,
            },
            { merge: true }
          );
        } catch (error) {
          console.error("Error updating dj document: ", error);
        }
      });
  };

  // Send YouTube video request to DJ
  const sendYoutubeRequestToDJ = async (video) => {
    if (!clientObj?.clientId) {
      alert("No DJ channel available");
      return;
    }

    // Clean the YouTube junk at the SOURCE — html entities, (Lyrics)/(Official
    // Video) descriptors, channel-name artists ("X - Topic", XVEVO, 7clouds…).
    // Everything downstream (request doc, pendingList, desktop library file)
    // inherits the clean pair. Recording descriptors like (Remix)/(Live)/
    // (Slowed + Reverb) are deliberately KEPT (2026-08-21 ruling).
    const cleaned = cleanYoutubeMeta(video.snippet.title, video.snippet.channelTitle);
    const trackItem = {
      id: video.id.videoId,
      track: cleaned.title,
      artist: cleaned.artist,
      coverart: video.snippet.thumbnails.medium?.url || video.snippet.thumbnails.default?.url,
      youTubeId: video.id.videoId,
      // Leave genre blank — the desktop resolves the real genre (local match /
      // Spotify guess) when it processes the request. Don't seed a placeholder.
      genre: '',
    };

    resetSearch();
    setRequestedTrack(trackItem);
    setIsOpen(!isOpen);
    sendThisPickToDJ(trackItem);
  };

  // Request a track from the DJ's desktop library (index hit, not in the
  // loaded playlist). Mirrors sendYoutubeRequestToDJ: build a track item,
  // close the search, run the same quota/frequency checks + processRequest.
  const sendLibraryPickToDJ = (libTrack) => {
    if (!clientObj?.clientId) {
      alert("No DJ channel available");
      return;
    }
    const trackItem = {
      id: libTrack.id,
      track: libTrack.t,
      artist: libTrack.a || "Unknown Artist",
      genre: libTrack.g || "",
      coverart: libTrack.c || "",
      youTubeId: "",
      source: "djlibrary",
    };
    resetSearch();
    setRequestedTrack(trackItem);
    setIsOpen(!isOpen);
    sendThisPickToDJ(trackItem);
  };

  const sendThisPickToDJ = async (item) => {
    // Should check if this user has fulfiled quota for requests
    // Should also check tht a track has reached limit of plays per config time range e.g. 20 minutes
    // console.log("sendThisPickToDJ", item);

    Promise.all([await userchecked(), await trackFreqChecked(item)]).then(
      (res) => {
        // console.log("user and track check", res);

        if (!res[0] || !res[1]) {
          // console.log("one of the above is false so no request sent");

          if (!res[0]) {
            alert("you must wait before you can make more requests");
          }

          if (!res[1]) {
            alert("sendThisPickToDJ: this track cannot be played yet, request a different track");
          }
        } else {
          processRequest(item);
        }
      }
    );
  };

  // useEffect(() => {
  //   console.log("tab changed", tab);
  // }, [tab]);

  const tabNavigation = (tab) => (event) => {
    event.preventDefault();

    // console.log("tabNavigation", tab);

    setTab(tab);
      // console.log('lets register this user as online from now, theyve used the naviagtion');
      // update the current users document with a timestamp
      const update = { lastOnline: serverTimestamp() };
      setDoc(doc(db, "users", auth.currentUser.uid), update, { merge: true });

    // if (tab === "home") {
    //   setShowServicesPanel(false);
    //   setAnimOne(false);
    // } else if (tab === "playing") {
    //   setShowServicesPanel(false);
    //   setAnimOne(false);
    // } else if (tab === "requests") {
    //   setShowServicesPanel(false);
    //   setAnimOne(false);
    // } else if (tab === "profile") {
    //   setShowServicesPanel(false);
    //   setAnimOne(false);
    // } else if (tab === "chat") {
    //   showChatlist();
    // }
  };

  const setRequestedTrackSetter = (item) => {
    // console.log("setRequestedTrackSetter", item);
    setRequestedTrack(item);
  };

  const showUser = (event, user) => {
    event.preventDefault();
    // console.log("showUser", user);
    setShowUserModal(true);
  };

  const showServices = (event) => {
    event.preventDefault();
    // console.log("showServices");
    setAnimOne(true);
    setTimeout(() => {
      setShowServicesPanel(true);
    }, 500);

    setTimeout(() => {
      setAnimOne(false);
    }, 1000);
  };

  const showJukebox = (event) => {
    event.preventDefault();
    // console.log("showJukebox");
    setAnimOne(true);
    setTimeout(() => {
      setShowServicesPanel(false);
    }, 500);
    setTimeout(() => {
      setAnimOne(false);
    }, 1000);
  };

  const scrollToPlayingTrack = (e) => {
    e.preventDefault();
    const rand = Math.floor(Math.random() * 1000);
    // console.log("triggered", rand);
    const triggerScroll = { triggerScroll: rand };
    setMessage(JSON.stringify(triggerScroll));
  };

  const viewPlayingTrack = (e) => {
    e.preventDefault();
    const idx = jukebox.findIndex((item) => item.id === currentSongId);
    if (idx !== -1) {
      setCurrentTrack(jukebox[idx]);
      setShowCurrentTrackModal(true);
    }
  };

  const viewNextTrack = (e) => {
    e.preventDefault();
    // open modal to reveal next track. get next track by using current index and adding 1 to it
    // use currentSongId
    // console.log("currentSongId", currentSongId);
    // console.log("jukebox", jukebox);
    const idx = jukebox.findIndex((item) => item.id === currentSongId);
    if (idx !== -1) {
      // console.log("current jukebox track and index", jukebox[idx], idx);
      // console.log("next song id is", jukebox[idx + 1]);
      setNextTrack(jukebox[idx + 1]);
      setShowNextTrackModal(true);
    }
  };
  const refreshNTV = () => {
    setShowNextTrackModal(false);
    setTimeout(() => {
      setShowNextTrackModal(true);
    }, 300);
  };

  const refreshChatList = () => {
    // setInChatList(false);
    // setTimeout(() => {
    //   setInChatList(true);
    // }, 300);
  };

  const AddChatUsers = () => {
    return (
      <div className="flex flex-col justify-left items-center add-chat-users">
        <div className="flex flex-row justify-left items-center">
          <button
            className="btn btn-primary text-white"
            onClick={() => {
              // console.log("add chat users clicked");
            }}
          >
            Add Chat Users
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="flex flex-col flex-auto sm:items-stretch md:item-stretch manuf-jukebox-panel eebbzz">
        {/* #1a1a1a */}

        <div
          className={`flex flex-col flex-auto flex-shrink-0 h-full ${
            animOne === true ? "anim-one" : ""
          }`}
          style={{ scrollbarColor: "#2e6db5 #1a1a1a" }}
        >
          {tab === "home" && <ClientInfo clientObj={clientObj} />}
          {/* The chat tab IS the rebuilt chat now (2026-08-31 verdict) —
              the legacy ChatList/Chat pair is retired from every surface.
              A conversation opened from elsewhere (Search Members →
              openChatById sets chatId2 + chatUsers) lands directly in it. */}
          {tab === "chat" && clientObj && (
            <div className="flex-1" style={{ minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <ChatV2
                clientObj={effectiveClientObj}
                initialChat={isInChat2 && chatId2 ? { chatId: chatId2, users: chatUsers || [] } : null}
              />
            </div>
          )}

          {tab === "playing" && (
            <NowPlaying
              nextSongObjectVotes={nextSongObjectVotes}
              userObj={userObj}
              clientObj={effectiveClientObj}
              trackObj={nextTrack}
              playlistId={playlistId}
              placeholderAvatar={jukeboxUserObj.photoURL}
              setShowNextTrackModal={setShowNextTrackModal}
              setCurrentPendingLength={setCurrentPendingLength}
              currentPendingLength={currentPendingLength}
              currentSongObj={currentSongObj}
              setTab={setTab}
              showDjModeModal={showDjModeModal}
              setShowDjModeModal={setShowDjModeModal}
              setRequestedTrack={setRequestedTrackSetter}
              pickTrack={pickTrack}
              highlightRequest={pendingHighlight}
              onHighlightConsumed={clearPendingHighlight}
            />
          )}
          {tab === "requests" && (
            <>
              <div className="flex flex-col overflow-x-auto mb-4 manuf-scrollable-section">
                <div className="flex flex-row  h-full">
                  <div className="text-white divide-y divide-gray-100 w-full  muuz-nowplaying-panel">
                    {/* Channel Guest Hosting: playlist tabs. Only renders
                        when this VM host has at least one accepted guest
                        DJ. Tabs = VM's own + each accepted guest. The
                        currently-active tab drives which playlist the
                        AccordionList shows; the LIVE tab (marked with a
                        red dot) is the only one that accepts requests. */}
                    {acceptedGuestDjs.length > 0 && (
                      <div
                        className="flex flex-row overflow-x-auto gap-2 py-2 px-1"
                        style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}
                      >
                        {[
                          {
                            uid: clientObj.clientId,
                            label:
                              clientObj.muuzboxAccountName ||
                              (userObj && userObj.channel) ||
                              "Host",
                          },
                          // Prefer the DJ's channel name for the tab label —
                          // djDisplayName can be an email (that's the
                          // fallback when the account has no display name),
                          // which is too long for a tab. Channel name is the
                          // short, brandable identifier guests recognise.
                          ...acceptedGuestDjs.map((g) => ({
                            uid: g.djUid,
                            label: g.djChannelName || g.djDisplayName || "Guest DJ",
                          })),
                        ].map((t) => {
                          const isActive = activePlaylistOwnerUid === t.uid;
                          const isLive = (liveGuestDj || clientObj.clientId) === t.uid;
                          return (
                            <button
                              key={t.uid}
                              type="button"
                              onClick={() => {
                                hasManuallyPickedTabRef.current = true;
                                setActivePlaylistOwnerUid(t.uid);
                              }}
                              className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors"
                              style={{
                                background: isActive
                                  ? "rgba(46,109,181,0.85)"
                                  : "rgba(255,255,255,0.06)",
                                color: isActive ? "#fff" : "rgba(255,255,255,0.75)",
                                border: isActive
                                  ? "1px solid rgba(46,109,181,1)"
                                  : "1px solid rgba(255,255,255,0.08)",
                              }}
                            >
                              {isLive && (
                                <span
                                  style={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: "50%",
                                    background: "#ef4444",
                                    boxShadow: "0 0 0 3px rgba(239,68,68,0.20)",
                                    display: "inline-block",
                                  }}
                                />
                              )}
                              {t.label}
                              {isLive && (
                                <span style={{ fontSize: 9, opacity: 0.85, letterSpacing: "0.05em" }}>
                                  LIVE
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {acceptedGuestDjs.length > 0 && !canRequest && (
                      <div
                        className="text-xs px-3 py-2"
                        style={{
                          background: "rgba(239,68,68,0.10)",
                          color: "rgba(255,255,255,0.85)",
                          borderBottom: "1px solid rgba(239,68,68,0.25)",
                        }}
                      >
                        Previewing playlist. Only the LIVE DJ accepts requests
                        right now — tap the LIVE tab to submit one.
                      </div>
                    )}

                    {/* search */}
                    <div className="flex flex-row  justify-between items-align items-center h-28 w-full p-0 m-0">
                      <div className="relative search-bar">
                        <input
                          ref={searchInputRef}
                          type="text"
                          placeholder={
                            isListening ? "listening…" : "search jukebox"
                          }
                          onFocus={() => setInputFocus(true)}
                          onChange={handleSubmit}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleSubmit(e);
                          }}
                          className="flex w-full text-white border rounded-xl focus:outline-none focus:border-indigo-300 pl-4 h-20 manuf-input"
                        />
                        {speechSupported && (
                          <button
                            type="button"
                            onClick={toggleListening}
                            aria-label={
                              isListening
                                ? "Stop voice search"
                                : "Search by voice"
                            }
                            title={
                              isListening
                                ? "Listening… tap to stop"
                                : "Tap and speak your request"
                            }
                            className={`mic-button ${
                              isListening ? "listening" : ""
                            }`}
                          >
                            <svg
                              viewBox="0 0 24 24"
                              fill="currentColor"
                              aria-hidden="true"
                            >
                              <path d="M12 15a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3z"></path>
                              <path d="M19 11a1 1 0 1 0-2 0 5 5 0 0 1-10 0 1 1 0 1 0-2 0 7 7 0 0 0 6 6.92V21a1 1 0 1 0 2 0v-3.08A7 7 0 0 0 19 11z"></path>
                            </svg>
                          </button>
                        )}
                      </div>
                      {/* <div className="ml-2 flex items-center justify-center">
                        <button
                          onClick={(event) => handleSubmit(event)}
                          className="flex items-center justify-center bg-indigo-500 hover:bg-indigo-600 rounded-xl text-white px-4 py-1 mt-2 flex-shrink-0 w-16 h-16"
                        >
                          <span className="ml-0">
                            <svg
                              className="w-6 h-6 transform rotate-45 -mt-px"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                              ></path>
                            </svg>
                          </span>
                        </button>
                      </div> */}
                    </div>
                    {/* search end */}

                    {!showJukeboxList && (
                      <AccordionList
                        data={groupedTracks}
                        setRequestedTrack={setRequestedTrackSetter}
                        pickTrack={pickTrack}
                        listTitle={"Music Tracks by Genre"}
                        mode={"dark"}
                      />
                    )}
                    {showJukeboxList && (
                      <JukeboxList4
                        jukebox={jukebox}
                        setRequestedTrack={setRequestedTrack}
                        pickTrack={pickTrack}
                        viewNextTrack={viewNextTrack}
                        youtubeVideos={showYoutubeResults ? videos : []}
                        onYoutubeVideoClick={sendYoutubeRequestToDJ}
                        djLibraryTracks={djLibraryResults}
                        onDjLibraryClick={sendLibraryPickToDJ}
                        isSearchingYoutube={isSearching}
                      />
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
          {tab === "profile" && (
            <div className="text-white w-full  muuz-nowplaying-panel">
              <EditUser
                userObj={userObj}
                setTab={setTab}
                onProfileUpdate={handleProfileUpdate}
                clientObj={clientObj}
              />
            </div>
          )}
          {showPrizeWinner && createPortal(
            <div
              onClick={() => setShowPrizeWinner(false)}
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.85)',
                zIndex: 99999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '16px',
                overflowY: 'auto',
              }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: '100%',
                  maxWidth: '420px',
                  maxHeight: '88vh',
                  overflowY: 'auto',
                  boxSizing: 'border-box',
                  background: 'linear-gradient(135deg, #ec4899 0%, #a855f7 100%)',
                  borderRadius: '20px',
                  padding: '16px 20px',
                  textAlign: 'center',
                  color: '#fff',
                  boxShadow: '0 20px 60px rgba(236, 72, 153, 0.4)',
                  animation: 'prizeWinnerPop 320ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                }}
              >
                <div style={{ fontSize: '36px', lineHeight: 1, marginBottom: '4px' }}>🎁</div>
                <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#fff5fa', marginBottom: '3px' }}>
                  Prize Draw Winner
                </div>
                <h2 style={{ margin: '0 0 5px', fontSize: '22px', fontWeight: 800 }}>
                  You've won!
                </h2>
                <p style={{ margin: '0 0 10px', fontSize: '13px', opacity: 0.95 }}>
                  You requested the prize draw track:
                </p>
                <div style={{ background: 'rgba(0,0,0,0.2)', borderRadius: '12px', padding: '10px 12px', marginBottom: '12px' }}>
                  <div style={{ fontWeight: 700, fontSize: '15px' }}>{prizeWinDetails?.trackTitle}</div>
                  <div style={{ fontSize: '13px', opacity: 0.9 }}>{prizeWinDetails?.trackArtist}</div>
                </div>
                {prizeWinDetails?.prizeDescription && (
                  <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: '10px', padding: '9px 12px', marginBottom: '12px', fontSize: '14px', fontWeight: 600 }}>
                    🎁 {prizeWinDetails.prizeDescription}
                  </div>
                )}
                {prizeClaimStatus === "prompt" && prizeClaim?.isAnonymous && (
                  <>
                    <p style={{ margin: '0 0 12px', fontSize: '13px', opacity: 0.95 }}>
                      Attach your <b>Google account</b> to claim it. If you don't, you forfeit the prize and it goes back into the draw for someone else.
                    </p>
                    <button
                      onClick={() => doClaimPrize()}
                      style={{ width: '100%', padding: '10px', background: '#fff', color: '#a855f7', border: 'none', borderRadius: '999px', fontWeight: 700, fontSize: '15px', cursor: 'pointer', marginBottom: '10px' }}
                    >
                      Claim with Google
                    </button>
                    <button
                      onClick={() => doDeclinePrize()}
                      style={{ width: '100%', padding: '10px', background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.55)', borderRadius: '999px', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}
                    >
                      No thanks — forfeit
                    </button>
                  </>
                )}
                {(prizeClaimStatus === "claiming" ||
                  (prizeClaimStatus === "prompt" && !prizeClaim?.isAnonymous)) && (
                  <p style={{ margin: '8px 0 0', fontSize: '14px', fontWeight: 600 }}>
                    Securing your prize…
                  </p>
                )}
                {prizeClaimStatus === "claimed" && (
                  <>
                    <p style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: 600 }}>
                      🎉 Prize secured! Show this code to venue staff to collect it.
                    </p>
                    {prizeClaim?.djchatId && auth.currentUser?.uid && (
                      <>
                        <div style={{ display: 'inline-block', background: '#fff', padding: 12, borderRadius: 14, marginBottom: 10 }}>
                          <QRCodeSVG
                            value={JSON.stringify(prizeQrPayload({
                              venueUid: DJChatId,
                              djchatId: prizeClaim.djchatId,
                              uid: auth.currentUser.uid,
                              prizeId: prizeClaim.prizeId,
                            }))}
                            size={168}
                            level="M"
                          />
                        </div>
                        <div style={{ fontSize: '16px', opacity: 0.85, marginBottom: 6, wordBreak: 'break-all' }}>
                          Code: {prizeCode({ venueUid: DJChatId, djchatId: prizeClaim.djchatId })}
                        </div>
                        <div style={{ fontSize: '16px', opacity: 0.85, marginBottom: 14 }}>
                          It's saved in your profile under <strong>My prizes</strong>.
                        </div>
                      </>
                    )}
                    <button
                      onClick={() => setShowPrizeWinner(false)}
                      style={{ width: '100%', padding: '10px', background: '#fff', color: '#a855f7', border: 'none', borderRadius: '999px', fontWeight: 700, fontSize: '15px', cursor: 'pointer' }}
                    >
                      Done
                    </button>
                  </>
                )}
                {prizeClaimStatus === "already_won" && (
                  <>
                    <p style={{ margin: '0 0 14px', fontSize: '14px', fontWeight: 600 }}>
                      Ah — this prize has already been won. Better luck next time!
                    </p>
                    <button
                      onClick={() => setShowPrizeWinner(false)}
                      style={{ width: '100%', padding: '10px', background: '#fff', color: '#a855f7', border: 'none', borderRadius: '999px', fontWeight: 700, fontSize: '15px', cursor: 'pointer' }}
                    >
                      Close
                    </button>
                  </>
                )}
                {prizeClaimStatus === "forfeited" && (
                  <>
                    <p style={{ margin: '0 0 14px', fontSize: '14px', fontWeight: 600 }}>
                      Prize forfeited — it's back in the draw for someone else.
                    </p>
                    <button
                      onClick={() => setShowPrizeWinner(false)}
                      style={{ width: '100%', padding: '10px', background: '#fff', color: '#a855f7', border: 'none', borderRadius: '999px', fontWeight: 700, fontSize: '15px', cursor: 'pointer' }}
                    >
                      Close
                    </button>
                  </>
                )}
                {prizeClaimStatus === "error" && (
                  <>
                    <p style={{ margin: '0 0 16px', fontSize: '14px', fontWeight: 600 }}>
                      {claimError || "Something went wrong claiming your prize. Please try again."}
                    </p>
                    <button
                      onClick={() => doClaimPrize()}
                      style={{ width: '100%', padding: '10px', background: '#fff', color: '#a855f7', border: 'none', borderRadius: '999px', fontWeight: 700, fontSize: '15px', cursor: 'pointer', marginBottom: '10px' }}
                    >
                      Try again
                    </button>
                    <button
                      onClick={() => setShowPrizeWinner(false)}
                      style={{ width: '100%', padding: '10px', background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.55)', borderRadius: '999px', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}
                    >
                      Close
                    </button>
                  </>
                )}
              </div>
              <style>{`
                @keyframes prizeWinnerPop {
                  0% { transform: scale(0.7); opacity: 0; }
                  100% { transform: scale(1); opacity: 1; }
                }
              `}</style>
            </div>,
            document.body
          )}
          {/* Prize-win celebration: money/stars rain (same as the Tip button).
              zIndex above the prize modal (99999) so it falls in front of it. */}
          <TipBurst fireKey={prizeCelebrate} zIndex={100000} />
          {isOpen && (
            <div
              className="bg-white divide-y divide-gray-100 rounded-lg shadow w-44 dark:bg-gray-700 muuz-confirm-panel"
              style={{
                animation: shiver ? "muuzShiver 0.5s linear" : "none",
                // A bit taller than the base 72cqb so the (now compacted) content
                // — heading, small coverart, points bar, mood box — all fit above
                // the fold on small phones (Samsung S20). Fixed height keeps the
                // panel centered without clipping its top.
                height: "82cqb",
              }}
            >
              <style>{`
                /* Keep the panel's base centering (translate(-50%,-50%)) so the
                   shake stays put — just a subtle ±2px x-axis shiver on top. */
                @keyframes muuzShiver {
                  0%,100% { transform: translate(-50%, -50%) translateX(0); }
                  20% { transform: translate(-50%, -50%) translateX(-2px); }
                  40% { transform: translate(-50%, -50%) translateX(2px); }
                  60% { transform: translate(-50%, -50%) translateX(-1.5px); }
                  80% { transform: translate(-50%, -50%) translateX(1px); }
                }
              `}</style>
              {/* Muuzbox logo pinned to the modal's top-left corner — moved out
                  of the content flow to save vertical space for the mood box. */}
              <img
                src="img/muuzbox-logo.png"
                alt="Muuzbox"
                style={{ position: "absolute", top: 8, left: 8, width: 22, height: 22, zIndex: 1 }}
              />
              <div
                className="py-2 text-sm text-gray-700 dark:text-gray-200"
                aria-labelledby="dropdownDefaultButton"
              >
                <div className="text-center">
                  <div
                    onClick={() => {
                      setIsOpen(false);
                      // Closing the confirmation → land on Now Playing so the
                      // new-request highlight + vote reminder play.
                      setTab("playing");
                      // Reset user selection when closing modal
                      setSelectedRequestUser(null);
                      setRequestUserSearch("");
                      setShowRequestUserDropdown(false);
                    }}
                    className="muuzbox-section-button"
                  >
                    {/* <div
                            id="notifyMuuzbox-close"
                            className="section-close muuzbox-close rounded"
                          ></div> */}

                    <div
                      id="notifyMuuzbox-close"
                      className="close-muuzbox-btn ion-close"
                    ></div>
                  </div>
                  <div className="block w-full text-lg text-center font-black mb-1">
                    <div>#{clientObj.id}</div>
                    <div className="uppercase text-red-800">
                      your request has been accepted!!
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setIsOpen(false);
                      setTab("playing");
                    }}
                    className="block w-full text-center px-4 py-1 hover:bg-gray-600 dark:hover:bg-gray-600 dark:hover:text-white muuzmenuitem"
                  >
                    <div className="fadeIn rounded-lg">
                      <img
                        className="rounded-lg coverart-ani"
                        src={requestedTrack.coverart}
                        alt={"cover art for " + requestedTrack.track}
                        style={{ width: 68, height: 68, maxWidth: "50%" }}
                      />
                    </div>

                    <div>
                      <strong>{requestedTrack.artist}</strong>
                    </div>
                    <div>{requestedTrack.track}</div>
                    {/* <div>{requestedTrack.coverart}</div> */}
                  </button>
                  
                  {/* Optional User Assignment Section — hidden for now (may return later) */}
                  {false && (
                  <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <h4 className="text-base font-semibold mb-2 text-gray-700 dark:text-gray-300">
                      Dedicate to your friend (optional)
                    </h4>
                    <div className="relative">
                      <input
                        id="requestUserSearch"
                        type="text"
                        value={requestUserSearch}
                        onChange={(e) => setRequestUserSearch(e.target.value)}
                        onFocus={() => requestUserSearch && setShowRequestUserDropdown(true)}
                        placeholder="Search for user..."
                        className="w-full p-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      />
                      
                      {showRequestUserDropdown && (
                        <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                          {filteredRequestUsers.length > 0 ? (
                            filteredRequestUsers.map((user) => (
                              <div
                                key={user.id}
                                className="flex items-center p-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer"
                                onClick={() => handleSelectRequestUser(user)}
                              >
                                <div className="w-8 h-8 rounded-full overflow-hidden mr-2">
                                  {user.photoURL ? (
                                    <img src={user.photoURL} alt={user.displayName} className="w-full h-full object-cover" />
                                  ) : (
                                    <div className="w-full h-full bg-indigo-500 flex items-center justify-center text-white text-sm font-semibold">
                                      {(user.displayName || user.email || "?")[0].toUpperCase()}
                                    </div>
                                  )}
                                </div>
                                <div className="flex-1">
                                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                    {user.displayName || "No name"}
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {user.email}
                                  </div>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="p-2 text-sm text-gray-500 dark:text-gray-400">
                              No users found
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Show selected user */}
                    {selectedRequestUser && (
                      <div className="mt-2 flex items-center p-2 bg-green-50 dark:bg-green-900 rounded-lg">
                        <div className="w-8 h-8 rounded-full overflow-hidden mr-2">
                          {selectedRequestUser.photoURL ? (
                            <img src={selectedRequestUser.photoURL} alt={selectedRequestUser.displayName} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full bg-green-500 flex items-center justify-center text-white text-sm font-semibold">
                              {(selectedRequestUser.displayName || selectedRequestUser.email || "?")[0].toUpperCase()}
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <span className="text-sm font-medium text-green-800 dark:text-green-200">
                            Assigned to: {selectedRequestUser.displayName || selectedRequestUser.email}
                          </span>
                        </div>
                        <button
                          onClick={() => {
                            setSelectedRequestUser(null);
                            setRequestUserSearch("");
                          }}
                          className="text-red-500 hover:text-red-700 ml-2"
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  </div>
                  )}
                </div>

                {messages && (
                  <>
                    {messages.map((message, i) => (
                      <div
                        className="flex flex-row items-center hover:bg-gray-100 rounded-xl p-2"
                        key={i}
                      >
                        <div className="flex items-center justify-center h-12 w-12 rounded-full border overflow-hidden bg-indigo-500 flex-shrink-0">
                          <img
                            src={avatars[message.uid]?.photoURL}
                            alt={avatars[message.uid]?.displayName}
                          />
                        </div>
                        <div className="ml-2 text-lg font-semibold">
                          {message.text}
                        </div>
                      </div>
                    ))}
                  </>
                )}

                {messages.length === 0 && (
                  <div className="fadeIn" style={{ textAlign: "center", padding: "2px 12px 4px" }}>
                    <div style={{ fontWeight: 800, color: "#E91E8C", fontSize: 15, lineHeight: 1 }}>
                      +1 point
                    </div>
                    {(() => {
                      const TIERS = [100, 500, 1000];
                      const p = Number(points) || 0;
                      const reached = TIERS.filter((t) => p >= t).length;
                      const next = TIERS[reached];
                      const prev = reached > 0 ? TIERS[reached - 1] : 0;
                      const pct = next ? Math.min(100, Math.max(0, ((p - prev) / (next - prev)) * 100)) : 100;
                      const remaining = next ? next - p : 0;
                      return (
                        <div style={{ marginTop: 8 }}>
                          <div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>
                            {next ? `${remaining.toLocaleString()} pts to next tier` : "Top tier reached 🎉"}
                          </div>
                          <div style={{ height: 8, borderRadius: 999, background: "rgba(0,0,0,0.12)", overflow: "hidden" }}>
                            <div
                              style={{
                                height: "100%",
                                width: animateProgress ? `${pct}%` : "0%",
                                borderRadius: 999,
                                background: "linear-gradient(90deg,#E91E8C,#7C3AED)",
                                transition: "width 1s ease",
                              }}
                            />
                          </div>
                          <div style={{ fontSize: 11, color: "#aaa", marginTop: 4 }}>{p.toLocaleString()} points</div>
                        </div>
                      );
                    })()}
                    {/* Clone of the Now Playing mood box, below the points bar.
                        Negative side margins cancel the fadeIn wrapper's 12px
                        padding so it spans the same full width as the coverart
                        button above it. */}
                    <div style={{ marginTop: 8, marginLeft: -12, marginRight: -12, paddingTop: 8, borderTop: "1px solid rgba(0,0,0,0.1)", textAlign: "left" }}>
                      <MoodSelector
                        inline
                        showTip={false}
                        clientObj={effectiveClientObj}
                        moodButtonDailyLimit={effectiveClientObj?.moodButtonDailyLimit || 1}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Profile Update Modal for First-Time Requesters */}
          {displayNameOpen && pendingRequest && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, paddingTop: '2vh' }}>
              <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4" style={{ maxWidth: '28rem' }}>
                {/* Header */}
                <div className="bg-gradient-to-r from-pink-500 to-red-500 p-3 rounded-t-lg">
                  <h3 className="text-lg font-bold text-white text-center">
                    Complete Your Profile
                  </h3>
                </div>

                {/* Content */}
                <div className="p-4" style={{ backgroundColor: '#ffffff' }}>
                  {/* Song Info - compact with coverart */}
                  <div className="mb-3 p-2 rounded-lg" style={{ backgroundColor: '#ffffff', border: '2px solid #000000' }}>
                    <div className="flex items-center">
                      <img
                        src={pendingRequest.coverart || FALLBACK_COVERART}
                        alt="cover art"
                        className="rounded-full flex-shrink-0 mr-3"
                        style={{ width: '44px', height: '44px' }}
                        onError={(e) => { e.target.onerror = null; e.target.src = FALLBACK_COVERART; }}
                      />
                      <div className="flex-1 overflow-hidden">
                        <p style={{ fontSize: '14px', fontWeight: 'bold', color: '#000000', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {pendingRequest.track}
                        </p>
                        <p style={{ fontSize: '12px', fontWeight: '500', color: '#666', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {pendingRequest.artist}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Display Name Input */}
                  <div className="mb-3">
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#000000', marginBottom: '4px' }}>
                      Choose Your Display Name
                    </label>
                    <input
                      type="text"
                      value={newDisplayName}
                      onChange={(e) => setNewDisplayName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleQuickProfileSave();
                        }
                      }}
                      placeholder="Enter your name..."
                      style={{
                        width: '100%',
                        padding: '10px',
                        fontSize: '16px',
                        fontWeight: '500',
                        color: '#000000',
                        backgroundColor: '#ffffff',
                        border: '2px solid #333333',
                        borderRadius: '8px',
                        outline: 'none'
                      }}
                      autoFocus
                    />
                    <p style={{ fontSize: '11px', color: '#666666', marginTop: '4px' }}>
                      This name will be visible to the DJ
                    </p>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setDisplayNameOpen(false);
                        setPendingRequest(null);
                      }}
                      style={{
                        flex: 1,
                        padding: '10px',
                        fontSize: '14px',
                        fontWeight: '600',
                        backgroundColor: '#e5e7eb',
                        color: '#374151',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleQuickProfileSave}
                      className="bg-gradient-to-r from-pink-500 to-red-500 hover:from-pink-600 hover:to-red-600"
                      style={{
                        flex: 1,
                        padding: '10px',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                      }}
                    >
                      Save & Request
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div
            className={`flex flex-row items-center h-16 w-full px-0 h-24 text-enter-fit ${
              inputFocus === true ? "text-enter-full" : ""
            }`}
          >
            <div className="tabbar">
              <div
                className={`tab ` + cn({ active: tab === "home" })}
                data-tab="home"
                onClick={tabNavigation("home")}
              >
                <svg
                  fill="#e2e8f0"
                  width="24px"
                  height="24px"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                  className={`tab-img-center ` + cn({ active: tab === "home" })}
                >
                  <path d="M12.56,2.171a1,1,0,0,0-1.12,0l-8,5.4A1,1,0,0,0,3,8.4V21a1,1,0,0,0,1,1H20a1,1,0,0,0,1-1V8.4a1,1,0,0,0-.44-.829ZM14,20H10V14h4Zm5,0H16V13a1,1,0,0,0-1-1H9a1,1,0,0,0-1,1v7H5V8.932l7-4.725,7,4.725Z" />
                </svg>

                <span>Home</span>
              </div>
              <div
                className={`tab ` + cn({ active: tab === "chat" })}
                data-tab="chats"
                onClick={tabNavigation("chat")}
              >
                <svg
                  fill="#e2e8f0"
                  width="24px"
                  height="24px"
                  viewBox="0 0 16 16"
                  id="chat-bubble-16px"
                  xmlns="http://www.w3.org/2000/svg"
                  className={`tab-img-center ` + cn({ active: tab === "chat" })}
                >
                  <path
                    id="Path_148"
                    data-name="Path 148"
                    d="M-20.5,16a.5.5,0,0,1-.354-.146.5.5,0,0,1-.131-.475l.912-3.648A8,8,0,0,1-21,8a8.009,8.009,0,0,1,8-8A8.009,8.009,0,0,1-5,8a8.009,8.009,0,0,1-8,8,7.993,7.993,0,0,1-3.731-.927l-3.648.912A.5.5,0,0,1-20.5,16Zm3.836-1.959a.506.506,0,0,1,.245.064A7.007,7.007,0,0,0-13,15,7.008,7.008,0,0,0-6,8a7.008,7.008,0,0,0-7-7,7.008,7.008,0,0,0-7,7,7.01,7.01,0,0,0,.9,3.419.5.5,0,0,1,.049.366l-.757,3.028,3.028-.757A.5.5,0,0,1-16.664,14.041Z"
                    transform="translate(21)"
                  />
                </svg>

                <span>Chats</span>
              </div>
              <div
                className={
                  `tab music-symbol glow` + cn({ active: tab === "playing" })
                }
                data-tab="playing"
                onClick={tabNavigation("playing")}
              >
                {/* <svg
                  fill="#888888"
                  width="24px"
                  height="24px"
                  viewBox="0 0 256 256"
                  id="Flat"
                  xmlns="http://www.w3.org/2000/svg"
                  className={
                    `tab-img-center ` + cn({ active: tab === "playing" })
                  }
                >
                  <path d="M215.38477,22.5415a12.00368,12.00368,0,0,0-10.29493-2.1831l-128,32A11.99928,11.99928,0,0,0,68,64V167.3501A39.99353,39.99353,0,1,0,92,204V73.36914l104-26v87.981A39.99353,39.99353,0,1,0,220,172V32A11.99843,11.99843,0,0,0,215.38477,22.5415ZM52,220a16,16,0,1,1,16-16A16.01833,16.01833,0,0,1,52,220Zm128-32a16,16,0,1,1,16-16A16.01833,16.01833,0,0,1,180,188Z" />
                </svg>
                <span>Now Playing</span> */}
                <div class="music-pulse">
                  <span class="circle circle1"></span>
                  <span class="circle circle2"></span>
                  <span class="music-note">
                    <svg
                      fill="#888888"
                      width="24px"
                      height="24px"
                      viewBox="0 0 256 256"
                      id="Flat"
                      xmlns="http://www.w3.org/2000/svg"
                      className={
                        `tab-img-center ` + cn({ active: tab === "playing" })
                      }
                    >
                      <path d="M215.38477,22.5415a12.00368,12.00368,0,0,0-10.29493-2.1831l-128,32A11.99928,11.99928,0,0,0,68,64V167.3501A39.99353,39.99353,0,1,0,92,204V73.36914l104-26v87.981A39.99353,39.99353,0,1,0,220,172V32A11.99843,11.99843,0,0,0,215.38477,22.5415ZM52,220a16,16,0,1,1,16-16A16.01833,16.01833,0,0,1,52,220Zm128-32a16,16,0,1,1,16-16A16.01833,16.01833,0,0,1,180,188Z" />
                    </svg>
                  </span>
                </div>
                <span
                  className={`music-pulse-text ${
                    tab === "playing" ? "active" : ""
                  }`}
                >
                  Now Playing
                </span>
              </div>

              <div
                className={`tab ` + cn({ active: tab === "requests" })}
                data-tab="request"
                onClick={tabNavigation("requests")}
              >
                <svg
                  width="24px"
                  height="24px"
                  viewBox="0 0 24 24"
                  fill="#e2e8f0"
                  xmlns="http://www.w3.org/2000/svg"
                  className={
                    `tab-img-center ` + cn({ active: tab === "requests" })
                  }
                >
                  <path
                    d="M6 12H18M12 6V18"
                    // stroke="#888888"
                    stroke={tab === "requests" ? "#fa163f" : "#888888"}
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <span>Request</span>
              </div>
              <div
                className={`tab ` + cn({ active: tab === "profile" })}
                data-tab="profile"
                onClick={tabNavigation("profile")}
              >
                <svg
                  width="24px"
                  height="24px"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                  className={
                    `tab-img-center ` + cn({ active: tab === "profile" })
                  }
                >
                  <title />

                  <g id="Complete">
                    <g id="user">
                      <g>
                        <path
                          d="M20,21V19a4,4,0,0,0-4-4H8a4,4,0,0,0-4,4v2"
                          fill="none"
                          // stroke="#000000"
                          stroke={tab === "profile" ? "#fa163f" : "#888888"}
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                        />

                        <circle
                          cx="12"
                          cy="7"
                          fill="none"
                          r="4"
                          // stroke="#000000"
                          stroke={tab === "profile" ? "#fa163f" : "#888888"}
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                        />
                      </g>
                    </g>
                  </g>
                </svg>
                <span>Profile</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
